import { InfoIcon } from 'lucide-react-native';
import { useState } from 'react';
import { type SceneRendererProps, TabView } from 'react-native-tab-view';
import { View, XStack, YStack } from 'tamagui';

import {
    Button,
    ButtonSize,
    ButtonVariant,
    IconButton,
} from '../../components/buttons/Button';
import {
    InsetContainer,
    InsetContainerVariant,
} from '../../components/containers/Containers';
import { ScreenContainer } from '../../components/containers/ScreenContainer';
import { Input } from '../../components/inputs/Input';
import { Notice } from '../../components/notices/Notice';
import {
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallRegular,
    H6Regular,
} from '../../components/text/Typography';

export const Containers = () => {
    return (
        <YStack gap={48}>
            <YStack gap={16}>
                <BodyLargeRegular>Inset Container</BodyLargeRegular>
                <InsetContainer>
                    <BodyMediumRegular
                        borderBottomWidth={1}
                        borderBottomColor="$gray300"
                    >
                        John Doe
                    </BodyMediumRegular>
                    <BodyMediumRegular
                        borderBottomWidth={1}
                        borderBottomColor="$gray300"
                    >
                        Jane Doe
                    </BodyMediumRegular>
                    <BodyMediumRegular
                        borderBottomWidth={1}
                        borderBottomColor="$gray300"
                    >
                        Sally Smith
                    </BodyMediumRegular>
                </InsetContainer>
            </YStack>
            <YStack gap={16}>
                <BodyLargeRegular>
                    Inset Container (Dark Variant)
                </BodyLargeRegular>
                <InsetContainer variant={InsetContainerVariant.DARK}>
                    <BodyMediumRegular
                        borderBottomWidth={1}
                        borderBottomColor="$gray300"
                    >
                        John Doe
                    </BodyMediumRegular>
                    <BodyMediumRegular
                        borderBottomWidth={1}
                        borderBottomColor="$gray300"
                    >
                        Jane Doe
                    </BodyMediumRegular>
                    <BodyMediumRegular
                        borderBottomWidth={1}
                        borderBottomColor="$gray300"
                    >
                        Sally Smith
                    </BodyMediumRegular>
                </InsetContainer>
            </YStack>

            <YStack gap={24} borderTopWidth={1} borderTopColor="$gray600">
                <YStack gap={8} marginTop={24}>
                    <BodyLargeRegular>Screen Container</BodyLargeRegular>
                    <BodySmallRegular maxWidth={600}>
                        ScreenContainer provides a consistent layout pattern for
                        full-screen views with optional header elements (title,
                        back button, close button, right action) and automatic
                        safe area handling. Content is scrollable by default
                        with a max width constraint for better readability.
                    </BodySmallRegular>
                </YStack>

                <YStack gap={16}>
                    <H6Regular>With Title and Subtitle</H6Regular>
                    <View
                        height={400}
                        borderWidth={1}
                        borderColor="$gray300"
                        borderRadius={8}
                        overflow="hidden"
                    >
                        <ScreenContainer
                            title="Account Settings"
                            subtitle="Manage your account preferences"
                            hasBackButton
                        >
                            <YStack gap={16}>
                                <BodyMediumRegular>
                                    This screen has both a title and a subtitle
                                    for additional context.
                                </BodyMediumRegular>
                                <Notice
                                    variant="positive"
                                    title="Success"
                                    description="Settings saved successfully"
                                />
                            </YStack>
                        </ScreenContainer>
                    </View>
                </YStack>

                <YStack gap={16}>
                    <H6Regular>With Close</H6Regular>
                    <View
                        height={400}
                        borderWidth={1}
                        borderColor="$gray300"
                        borderRadius={8}
                        overflow="hidden"
                    >
                        <ScreenContainer
                            title="Transaction Details"
                            hasCloseButton
                            onClose={() => {
                                console.log('Back pressed');
                            }}
                        >
                            <YStack gap={16}>
                                <BodyMediumRegular>
                                    This screen includes a close button for
                                    navigation. You&apos;ll likely want to use
                                    either a close button or a back button.
                                </BodyMediumRegular>
                                <Notice
                                    variant="neutral"
                                    title="Transaction"
                                    description="Payment to merchant"
                                />
                            </YStack>
                        </ScreenContainer>
                    </View>
                </YStack>
                <YStack gap={16}>
                    <H6Regular>With Right Action</H6Regular>
                    <View
                        height={400}
                        borderWidth={1}
                        borderColor="$gray300"
                        borderRadius={8}
                        overflow="hidden"
                    >
                        <ScreenContainer
                            title="Edit Profile"
                            hasBackButton={true}
                            rightAction={
                                <IconButton
                                    variant="tertiary"
                                    icon={InfoIcon}
                                    onPress={() => {
                                        console.log('Info pressed');
                                    }}
                                />
                            }
                        >
                            <YStack gap={16}>
                                <BodyMediumRegular>
                                    You can add a custom right action component,
                                    such as an info button, save button, or any
                                    other action.
                                </BodyMediumRegular>
                                <Notice
                                    variant="neutral"
                                    title="Profile"
                                    description="Update your information"
                                />
                            </YStack>
                        </ScreenContainer>
                    </View>
                </YStack>

                <YStack gap={16}>
                    <H6Regular>Complete Example</H6Regular>
                    <BodySmallRegular maxWidth={600}>
                        This example shows a screen with back button, title,
                        subtitle, and a right action all together.
                    </BodySmallRegular>
                    <View
                        height={400}
                        borderWidth={1}
                        borderColor="$gray300"
                        borderRadius={8}
                        overflow="hidden"
                    >
                        <ScreenContainer
                            title="Payment Setup"
                            subtitle="Configure payment methods"
                            hasBackButton={true}
                            rightAction={
                                <IconButton
                                    variant="tertiary"
                                    icon={InfoIcon}
                                    onPress={() => {
                                        console.log('Info pressed');
                                    }}
                                />
                            }
                        >
                            <YStack gap={16}>
                                <BodyMediumRegular>
                                    This demonstrates all header elements
                                    working together: back button on the left,
                                    centered title with subtitle, and a custom
                                    action on the right.
                                </BodyMediumRegular>
                                <Notice
                                    variant="neutral"
                                    title="Add Payment"
                                    description="Link your bank account or card"
                                />
                            </YStack>
                        </ScreenContainer>
                    </View>
                </YStack>

                <YStack gap={16}>
                    <H6Regular>With TabView (Multi-Step Workflows)</H6Regular>
                    <BodySmallRegular maxWidth={600}>
                        For multi-step workflows, use TabView inside
                        ScreenContainer. The back button navigates through steps
                        first, then exits. This pattern is common for
                        onboarding, setup flows, and multi-page forms.
                    </BodySmallRegular>
                    <View
                        height={500}
                        borderWidth={1}
                        borderColor="$gray300"
                        borderRadius={8}
                        overflow="hidden"
                    >
                        <TabViewExample />
                    </View>
                </YStack>
            </YStack>

            <YStack gap={16}>
                <BodyLargeRegular>Safe Area View</BodyLargeRegular>
                <BodyMediumRegular maxWidth={400}>
                    The purpose of SafeAreaView is to render content within the
                    safe area boundaries of a device, specifically iOS.
                </BodyMediumRegular>
            </YStack>
            <YStack gap={16}>
                <BodyLargeRegular>Page Below Header</BodyLargeRegular>
                <BodyMediumRegular maxWidth={400}>
                    A container for page content that is rendered below a
                    header.
                </BodyMediumRegular>
            </YStack>
            <YStack gap={16}>
                <BodyLargeRegular>Smoothie Scroll View</BodyLargeRegular>
                <BodyMediumRegular maxWidth={400}>
                    A component that allows you to display content that exceeds
                    the screen&apos;s height or width, providing a scrollable
                    container for your elements.
                </BodyMediumRegular>
            </YStack>
            <YStack gap={16}>
                <BodyLargeRegular>Full Screen Container</BodyLargeRegular>
                <BodyMediumRegular maxWidth={400}>
                    A full screen container with max width applied for
                    cross-platform layouts.
                </BodyMediumRegular>
            </YStack>
        </YStack>
    );
};

enum SetupSteps {
    WELCOME = 'welcome',
    PERSONAL_INFO = 'personalInfo',
    COMPLETE = 'complete',
}

const TabViewExample = () => {
    const [index, setIndex] = useState(0);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');

    const routes = Object.values(SetupSteps).map((step) => ({
        key: step,
    }));

    const getRouteIndex = (step: SetupSteps) =>
        routes.findIndex(({ key }) => key === step);

    const welcomeIndex = getRouteIndex(SetupSteps.WELCOME);
    const personalInfoIndex = getRouteIndex(SetupSteps.PERSONAL_INFO);
    const completeIndex = getRouteIndex(SetupSteps.COMPLETE);

    const getTabTitle = (currentIndex: number) => {
        switch (currentIndex) {
            case welcomeIndex:
                return 'Welcome';
            case personalInfoIndex:
                return 'Personal Info';
            case completeIndex:
                return 'All Set!';
            default:
                return '';
        }
    };

    const onPressBack = () => {
        if (index !== welcomeIndex) {
            // Navigate back through steps first
            setIndex(index - 1);
        } else {
            // Then exit (would use router.back() in real app)
            console.log('Exit workflow');
        }
    };

    const renderScene = ({
        route,
    }: SceneRendererProps & { route: { key: SetupSteps } }) => {
        // Only render the current scene for performance
        if (routes[index]?.key !== route.key) {
            return null;
        }

        const Content = (() => {
            switch (route.key) {
                case SetupSteps.WELCOME:
                    return (
                        <YStack
                            flex={1}
                            justifyContent="space-between"
                            gap={16}
                        >
                            <YStack gap={16}>
                                <BodyMediumRegular>
                                    This is a multi-step workflow using TabView.
                                    The title changes based on the current step,
                                    and the back button navigates through steps
                                    before exiting.
                                </BodyMediumRegular>
                            </YStack>
                            <Button
                                onPress={() => {
                                    setIndex(personalInfoIndex);
                                }}
                                size={ButtonSize.LARGE}
                                variant={ButtonVariant.PRIMARY}
                                text="Get Started"
                            />
                        </YStack>
                    );
                case SetupSteps.PERSONAL_INFO:
                    return (
                        <YStack
                            flex={1}
                            justifyContent="space-between"
                            gap={16}
                        >
                            <YStack gap={16}>
                                <BodyMediumRegular>
                                    Enter your information to continue.
                                </BodyMediumRegular>
                                <Input
                                    label="Name"
                                    placeholder="Enter your name"
                                    value={name}
                                    onChangeText={setName}
                                />
                                <Input
                                    label="Email"
                                    placeholder="Enter your email"
                                    value={email}
                                    onChangeText={setEmail}
                                />
                            </YStack>
                            <XStack gap={8}>
                                <Button
                                    onPress={() => {
                                        setIndex(welcomeIndex);
                                    }}
                                    size={ButtonSize.LARGE}
                                    variant={ButtonVariant.SECONDARY}
                                    text="Back"
                                    width="auto"
                                    flex={1}
                                />
                                <Button
                                    onPress={() => {
                                        setIndex(completeIndex);
                                    }}
                                    size={ButtonSize.LARGE}
                                    variant={ButtonVariant.PRIMARY}
                                    text="Continue"
                                    width="auto"
                                    flex={1}
                                />
                            </XStack>
                        </YStack>
                    );
                case SetupSteps.COMPLETE:
                    return (
                        <YStack
                            flex={1}
                            justifyContent="space-between"
                            gap={16}
                        >
                            <YStack gap={16}>
                                <BodyMediumRegular>
                                    Name: {name || 'Not provided'}
                                </BodyMediumRegular>
                                <BodyMediumRegular>
                                    Email: {email || 'Not provided'}
                                </BodyMediumRegular>
                            </YStack>
                            <Button
                                onPress={() => {
                                    console.log('Done - navigate to app');
                                }}
                                size={ButtonSize.LARGE}
                                variant={ButtonVariant.PRIMARY}
                                text="Done"
                            />
                        </YStack>
                    );
                default:
                    return <></>;
            }
        })();

        return <YStack flex={1}>{Content}</YStack>;
    };

    return (
        <ScreenContainer
            hasBackButton={true}
            onBack={onPressBack}
            title={getTabTitle(index)}
        >
            <TabView
                swipeEnabled={false}
                navigationState={{ index, routes }}
                onIndexChange={setIndex}
                renderScene={renderScene}
                renderTabBar={() => <></>}
            />
        </ScreenContainer>
    );
};
